import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import * as S from './styles';

interface LightboxProps {
	imageUrl: string | null;
	onClose: () => void;
}

const Lightbox = ({ imageUrl, onClose }: LightboxProps) => {
	const [isLoading, setIsLoading] = useState(true);
	const [imageError, setImageError] = useState(false);
	const [prevImageUrl, setPrevImageUrl] = useState<string | null>(null);

	// Synchronous setState-during-render: React supports calling setState during the render
	// pass when deriving state from props. This resets isLoading/imageError *before* the DOM
	// commits, avoiding a race condition where a cached image's onLoad fires before a
	// useEffect-based reset (which runs after paint) — leaving the lightbox stuck on a loader.
	if (imageUrl !== prevImageUrl) {
		setPrevImageUrl(imageUrl);
		if (imageUrl) {
			setIsLoading(true);
			setImageError(false);
		}
	}

	useEffect(() => {
		if (!imageUrl) return;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleEscape);
		return () => {
			window.removeEventListener('keydown', handleEscape);
		};
	}, [imageUrl, onClose]);

	if (!imageUrl) {
		return null;
	}

	const handleImageLoad = () => {
		setIsLoading(false);
	};

	const handleImageError = () => {
		setIsLoading(false);
		setImageError(true);
	};

	const handleOverlayClick = (e: React.MouseEvent) => {
		// Close if clicking the overlay itself, not the image
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return createPortal(
		<S.Overlay onClick={handleOverlayClick}>
			<S.ImageContainer>
				{isLoading && (
					<S.LoadingContainer>
						<Loader width={48} height={48} />
					</S.LoadingContainer>
				)}
				{imageError ? (
					<S.LoadingContainer>
						<div style={{ color: 'white' }}>Failed to load image</div>
					</S.LoadingContainer>
				) : (
					<>
						<S.Image
							src={imageUrl}
							alt="Attachment preview"
							onLoad={handleImageLoad}
							onError={handleImageError}
							style={{ display: isLoading ? 'none' : 'block' }}
						/>
						<S.CloseButton onClick={onClose}>
							<Icon type={IconType.CLOSE} width={24} height={24} />
						</S.CloseButton>
					</>
				)}
			</S.ImageContainer>
		</S.Overlay>,
		document.body
	);
};

export default Lightbox;
